class HoverZoom {
  constructor() {
    this.zoomResultContainer = null;
    this.resultImg = null;
    this.activeWrapper = null;
    this.activeImg = null;
    this.activeLens = null;
    this.isZooming = false;
    this.cx = 0;
    this.cy = 0;
    this.initialized = false;
    this.boundHandlers = new WeakMap();
  }

  /**
   * Create the zoom result container (only once)
   */
  createZoomContainer() {
    if (this.zoomResultContainer) return;

    this.zoomResultContainer = document.createElement('div');
    this.zoomResultContainer.id = 'hover-zoom-result';
    
    this.resultImg = document.createElement('img');
    this.resultImg.src = '';
    this.resultImg.alt = 'Zoomed product image';
    this.resultImg.loading = 'lazy';
    
    // Ensure image has proper initial positioning
    this.resultImg.style.position = 'absolute';
    this.resultImg.style.left = '0px';
    this.resultImg.style.top = '0px';
    
    this.zoomResultContainer.appendChild(this.resultImg);
    document.body.appendChild(this.zoomResultContainer);
  }

  /**
   * Calculate zoom ratio based on lens and result container sizes
   */
  updateZoomRatio(lens) {
    if (!this.zoomResultContainer || !lens) return;
    
    // Force a reflow to ensure dimensions are calculated
    const containerWidth = this.zoomResultContainer.offsetWidth || 450;
    const containerHeight = this.zoomResultContainer.offsetHeight || 450;
    const lensWidth = lens.offsetWidth || 250;
    const lensHeight = lens.offsetHeight || 250;
    
    console.log('updateZoomRatio:', {
      containerWidth,
      containerHeight,
      lensWidth,
      lensHeight
    });
    
    this.cx = containerWidth / lensWidth;
    this.cy = containerHeight / lensHeight;
    
    console.log('Calculated ratios:', { cx: this.cx, cy: this.cy });
  }

  /**
   * Handle mouse enter on image
   */
  handleMouseEnter(wrapper, img, lens) {
    // Don't activate on mobile/tablet
    if (window.innerWidth <= 1023) return;

    this.isZooming = true;
    this.activeWrapper = wrapper;
    this.activeImg = img;
    this.activeLens = lens;
    
    // Show elements first so they have dimensions
    lens.style.display = 'block';
    this.zoomResultContainer.style.display = 'block';
    
    // Now calculate zoom ratio after elements are visible
    this.updateZoomRatio(lens);

    // Load high-res image from data attribute
    const zoomImageSrc = img.getAttribute('data-zoom-image') || img.src;
    
    if (this.resultImg.src !== zoomImageSrc) {
      this.resultImg.style.opacity = '0';
      this.resultImg.src = zoomImageSrc;
      this.resultImg.alt = img.alt || 'Zoomed product image';
      
      this.resultImg.onload = () => {
        this.resultImg.style.opacity = '1';
      };
    }
  }

  /**
   * Handle mouse leave from image
   */
  handleMouseLeave(lens) {
    this.isZooming = false;
    this.activeWrapper = null;
    this.activeImg = null;
    this.activeLens = null;
    lens.style.display = 'none';
    
    if (this.zoomResultContainer) {
      this.zoomResultContainer.style.display = 'none';
    }
  }

  /**
   * Handle mouse move on image
   */
  handleMouseMove(e) {
    if (!this.isZooming || !this.activeImg || !this.activeLens) {
      return;
    }

    const img = this.activeImg;
    const lens = this.activeLens;
    
    if (!img || !lens || !this.resultImg || !this.zoomResultContainer) {
      return;
    }
    
    const bounds = img.getBoundingClientRect();

    console.log('is moving');
    
    let x = e.clientX - bounds.left;
    let y = e.clientY - bounds.top;

    // Prevent lens from going outside image boundaries
    x = Math.max(lens.offsetWidth / 2, Math.min(x, bounds.width - lens.offsetWidth / 2));
    y = Math.max(lens.offsetHeight / 2, Math.min(y, bounds.height - lens.offsetHeight / 2));

    // Position lens
    lens.style.left = (x - lens.offsetWidth / 2) + 'px';
    lens.style.top = (y - lens.offsetHeight / 2) + 'px';

    // Calculate zoom ratio if not already set or if NaN
    if (this.cx === 0 || this.cy === 0 || isNaN(this.cx) || isNaN(this.cy)) {
      this.updateZoomRatio(lens);
    }
    
    // If still NaN, something is wrong - don't continue
    if (isNaN(this.cx) || isNaN(this.cy)) {
      console.error('Zoom ratio is NaN, cannot calculate position');
      return;
    }

    // Position zoomed image in the fixed container
    const leftPos = -(x * this.cx - this.zoomResultContainer.offsetWidth / 2);
    const topPos = -(y * this.cy - this.zoomResultContainer.offsetHeight / 2);
    
    console.log('Positioning:', {
      x, y,
      cx: this.cx,
      cy: this.cy,
      leftPos,
      topPos,
      hasResultImg: !!this.resultImg,
      resultImgSrc: this.resultImg?.src,
      resultImgElement: this.resultImg
    });
    
    // Apply positioning to zoom result image with setProperty for higher priority
    this.resultImg.style.setProperty('left', leftPos + 'px', 'important');
    this.resultImg.style.setProperty('top', topPos + 'px', 'important');
    this.resultImg.style.setProperty('width', (bounds.width * this.cx) + 'px', 'important');
    this.resultImg.style.setProperty('height', (bounds.height * this.cy) + 'px', 'important');
    
    // Debug: Log what styles were actually applied
    console.log('Applied styles:', {
      left: this.resultImg.style.left,
      top: this.resultImg.style.top,
      width: this.resultImg.style.width,
      height: this.resultImg.style.height
    });
  }

  /**
   * Attach event listeners to all image media items
   */
  attachEventListeners() {
    const mediaItems = document.querySelectorAll('.product__media-item[data-media-type="image"]');
    
    mediaItems.forEach(mediaItem => {
      const wrapper = mediaItem.querySelector('.hover-zoom-wrapper');
      if (!wrapper) return;

      const img = wrapper.querySelector('.zoom-source-image');
      const lens = wrapper.querySelector('.hover-zoom-lens');

      if (!img || !lens) return;

      // Check if we've already attached listeners to this wrapper
      if (this.boundHandlers.has(wrapper)) {
        return; // Skip if already initialized
      }

      // Create bound handler functions
      const handleMouseEnter = () => {
        this.handleMouseEnter(wrapper, img, lens);
      };

      const handleMouseLeave = () => {
        this.handleMouseLeave(lens);
      };

      const handleMouseMove = (e) => {
        this.handleMouseMove(e);
      };

      // Store handlers so we can check if already bound
      this.boundHandlers.set(wrapper, {
        mouseenter: handleMouseEnter,
        mouseleave: handleMouseLeave,
        mousemove: handleMouseMove
      });

      // Attach event listeners
      wrapper.addEventListener('mouseenter', handleMouseEnter);
      wrapper.addEventListener('mouseleave', handleMouseLeave);
      wrapper.addEventListener('mousemove', handleMouseMove);
    });
  }

  /**
   * Initialize hover zoom functionality
   */
  init() {
    // Create zoom container if it doesn't exist
    this.createZoomContainer();
    
    // Attach event listeners
    this.attachEventListeners();
    
    this.initialized = true;
  }

  /**
   * Re-initialize (useful for dynamic content)
   */
  reinit() {
    this.attachEventListeners();
  }
}

// Create a single instance
const hoverZoom = new HoverZoom();

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => hoverZoom.init());
} else {
  hoverZoom.init();
}

// Re-initialize on window load (for lazy-loaded images)
window.addEventListener('load', () => hoverZoom.reinit());

// Re-initialize when Swiper slider changes
document.addEventListener('swiperslidechange', () => {
  setTimeout(() => hoverZoom.reinit(), 100);
});

// Re-initialize when images are lazy loaded
document.addEventListener('lazyloaded', () => {
  setTimeout(() => hoverZoom.reinit(), 100);
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HoverZoom;
}